import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';
import type Player from 'video.js/dist/types/player';
import { useScormTracking } from '@libertify/scorm';

/**
 * SCORM packaging config for the chaptered player. Omit (or `enabled:
 * false`) for the normal, non-SCORM player.
 */
export interface ScormPlayerConfig {
  /** Turn SCORM tracking on. */
  enabled: boolean;
  /**
   * Block forward seeks past the furthest naturally-watched position. The
   * lock (and the no-seek UI) lifts automatically on completion.
   */
  disableSeek?: boolean;
  /** Completion threshold as a fraction of duration (0..1). */
  threshold?: number;
  /** Pause playback when the tab/window loses focus (LMS attention check). */
  pauseOnBlur?: boolean;
}

interface UseScormPlayerArgs {
  /** SCORM config from the launch URL; `undefined` → fully inert hook. */
  scorm: ScormPlayerConfig | undefined;
  /** Ref to the live Video.js player owned by the host component. */
  playerRef: MutableRefObject<Player | null>;
  /** Playback rates restored when the seek lock lifts on completion. */
  playbackRates: number[];
}

/**
 * Bridges `@libertify/scorm`'s player-agnostic `useScormTracking` to the
 * Video.js-specific player chrome: the `.vjs-no-seek` skin (added while the
 * forward-seek lock is active, removed on completion), the speed-menu
 * restore, and the optional pause-on-blur attention check.
 *
 * Kept out of `@libertify/scorm` on purpose — that lib stays player-agnostic;
 * everything Video.js-specific lives here in experience-viewer.
 *
 * Returns:
 *  - `disableSeek` — whether the no-seek skin applies (drives the control-bar
 *    options so the speed menu is hidden while locked), and
 *  - `applyOnReady(player)` — call from the player's `ready` callback to
 *    attach tracking and (if locked) the no-seek class.
 *
 * Inert when `scorm` is undefined / `enabled: false` — `useScormTracking`
 * is called with `enabled: false`, so the normal player sees zero change.
 */
export function useScormPlayer({
  scorm,
  playerRef,
  playbackRates,
}: UseScormPlayerArgs): {
  disableSeek: boolean;
  applyOnReady: (player: Player) => void;
} {
  const disableSeek = scorm?.enabled === true && scorm.disableSeek === true;

  // Once the lesson is completed (this session or a previous one), all seek
  // restrictions stop applying: drop the no-seek skin and restore the menu.
  const completedRef = useRef(false);
  const unlockSeekUi = useCallback(
    (player: Player) => {
      player.removeClass('vjs-no-seek');
      try {
        const unlockable = player as unknown as {
          playbackRates?: (rates: number[]) => void;
          controlBar?: {
            getChild: (name: string) => unknown;
            addChild: (name: string) => unknown;
          };
        };
        unlockable.playbackRates?.(playbackRates);
        if (!unlockable.controlBar?.getChild('PlaybackRateMenuButton')) {
          unlockable.controlBar?.addChild('PlaybackRateMenuButton');
        }
      } catch {
        // Player torn down mid-unlock; next mount applies the unlocked UI.
      }
    },
    [playbackRates],
  );

  const handleCompleted = useCallback(() => {
    completedRef.current = true;
    const player = playerRef.current;
    if (player && !player.isDisposed()) unlockSeekUi(player);
  }, [playerRef, unlockSeekUi]);

  // The SCORM hook owns the full watch-integrity logic: watched-coverage
  // tracking, completion, resume (lesson_location + suspend_data) and the
  // forward-seek lock. enabled:false makes it inert.
  const { setupPlayerTracking } = useScormTracking({
    enabled: scorm?.enabled === true,
    completionThreshold: scorm?.threshold,
    disableSeek,
    onCompleted: handleCompleted,
  });

  // Opt-in (`scorm.pauseOnBlur`): pause when the player loses the user's
  // attention — tab switched (visibilitychange) or another window/app focused
  // (blur). Off by default so the standard player keeps playing.
  useEffect(() => {
    if (!scorm?.pauseOnBlur) return;
    const pausePlayback = () => {
      const player = playerRef.current;
      if (!player || player.isDisposed?.()) return;
      try {
        if (!player.paused()) player.pause();
      } catch {
        // Player mid-teardown; nothing to pause.
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') pausePlayback();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    globalThis.addEventListener('blur', pausePlayback);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalThis.removeEventListener('blur', pausePlayback);
    };
  }, [playerRef, scorm?.pauseOnBlur]);

  // Call from the player's `ready` callback: attach tracking, then (if the
  // seek lock applies) mount straight into the unlocked UI when already
  // completed, otherwise add the no-seek skin.
  const applyOnReady = useCallback(
    (player: Player) => {
      setupPlayerTracking(
        player as unknown as Parameters<typeof setupPlayerTracking>[0],
      );
      if (disableSeek) {
        if (completedRef.current) unlockSeekUi(player);
        else player.addClass('vjs-no-seek');
      }
    },
    [setupPlayerTracking, disableSeek, unlockSeekUi],
  );

  return { disableSeek, applyOnReady };
}
